import axios from 'axios';

/**
 * Order Protection Module
 * Handles toggling order protection product in cart
 */
class OrderProtection {
  constructor() {
    this.checkboxes = document.querySelectorAll('.order-protection__checkbox');
    this.isUpdating = false;

    // Bind the handler so we can remove it later
    this.boundHandleToggle = (e) => this.handleToggle(e);

    this.init();
  }

  init() {
    // Re-query checkboxes in case they were re-rendered
    this.checkboxes = document.querySelectorAll('.order-protection__checkbox');

    if (!this.checkboxes.length) return;

    this.checkboxes.forEach((checkbox) => {
      // Remove existing listener to prevent duplicates
      checkbox.removeEventListener('change', this.boundHandleToggle);

      // Add new listener
      checkbox.addEventListener('change', this.boundHandleToggle);
    });
  }

  async handleToggle(event) {
    if (this.isUpdating) {
      event.preventDefault();
      return;
    }

    const checkbox = event.target;
    const variantId = checkbox.dataset.protectionVariantId;
    const isChecked = checkbox.checked;

    // Check if this checkbox is in the cart drawer or cart page
    const isInDrawer = checkbox.closest('cart-drawer') !== null;

    this.isUpdating = true;
    this.setLoadingState(true);

    try {
      if (isChecked) {
        await this.addProtectionToCart(variantId);
      } else {
        await this.removeProtectionFromCart(variantId);
      }

      // Refresh cart UI
      await this.refreshCart(isInDrawer);
    } catch (error) {
      console.error('Order protection error:', error);
      // Revert checkbox state on error
      checkbox.checked = !isChecked;
      this.showError();
    } finally {
      this.isUpdating = false;
      this.setLoadingState(false);
    }
  }

  async addProtectionToCart(variantId) {
    const formData = {
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
    };

    const response = await axios.post('/cart/add.js', formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  }

  async removeProtectionFromCart(variantId) {
    // First, get the current cart to find the line item
    const cartResponse = await axios.get('/cart.js');
    const cart = cartResponse.data;

    // Find the protection product in cart
    const protectionItem = cart.items.find(
      (item) => item.variant_id === parseInt(variantId)
    );

    if (!protectionItem) return;

    // Remove the item by setting quantity to 0
    const formData = {
      updates: {
        [protectionItem.key]: 0,
      },
    };

    const response = await axios.post('/cart/update.js', formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  }

  async refreshCart(isInDrawer) {
    if (isInDrawer) {
      // We're in the cart drawer - refresh it via AJAX
      await this.refreshCartDrawer();
    } else {
      // We're on the cart page - just reload
      window.location.reload();
    }
  }

  async refreshCartDrawer() {
    try {
      // Fetch the cart drawer section HTML
      const response = await fetch('/?sections=cart-drawer');
      const data = await response.json();

      if (data['cart-drawer']) {
        // Create a temporary container
        const temp = document.createElement('div');
        temp.innerHTML = data['cart-drawer'];

        // Find the cart drawer element
        const newCartDrawer = temp.querySelector('cart-drawer');
        const currentCartDrawer = document.querySelector('cart-drawer');

        if (newCartDrawer && currentCartDrawer) {
          // Replace the inner content to maintain the open state
          const newInner = newCartDrawer.querySelector('.side-panel-inner');
          const currentInner = currentCartDrawer.querySelector('.side-panel-inner');

          if (newInner && currentInner) {
            currentInner.innerHTML = newInner.innerHTML;

            // Re-initialize the order protection
            this.init();

            // Dispatch event for other scripts
            document.dispatchEvent(new CustomEvent('cart:updated'));
          }
        }
      }

      // Update cart count in header
      await this.updateCartCount();
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  async updateCartCount() {
    try {
      const cartResponse = await axios.get('/cart.js');
      const cart = cartResponse.data;

      // Update cart count bubbles
      const cartCounts = document.querySelectorAll('.cart-count, .header-cart-count');
      cartCounts.forEach((element) => {
        element.textContent = cart.item_count;
        element.style.display = cart.item_count > 0 ? '' : 'none';
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  setLoadingState(isLoading) {
    const protectionElements = document.querySelectorAll('.order-protection');

    protectionElements.forEach((element) => {
      if (isLoading) {
        element.classList.add('order-protection--loading');
      } else {
        element.classList.remove('order-protection--loading');
      }
    });
  }

  showError() {
    // You can customize this to show a better error message
    alert('Er is iets misgegaan. Probeer het opnieuw.');
  }
}

// Singleton instance
let orderProtectionInstance = null;

// Initialize when DOM is ready
function initOrderProtection() {
  if (!orderProtectionInstance) {
    orderProtectionInstance = new OrderProtection();
  } else {
    // Just re-initialize existing instance
    orderProtectionInstance.init();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOrderProtection);
} else {
  initOrderProtection();
}

// Re-initialize after cart updates (for cart drawer)
document.addEventListener('cart:updated', () => {
  if (orderProtectionInstance) {
    orderProtectionInstance.init();
  }
});

export default OrderProtection;
